<script>
import { mapGetters, mapActions } from "vuex";

import sizeVue from "@/mixins/size.vue";

export default {
  props: {
    value: Number,
  },

  data: () => ({
    isLoading: false,

    search: null,

    options: {},
    totalItemsCount: 0,
    itemsPerPage: 20,
    page: 1,

    isExport: false,
  }),

  mixins: [sizeVue],

  computed: {
    tableHeight: (vm) =>
      vm.size.width >= 1200 ? vm.size.height - 370 : "auth",

    ...mapGetters("admin-masters", ["GET_APPLICATIONS"]),
    items: (vm) => vm.GET_APPLICATIONS,

    ...mapGetters("auth", ["GET_USER"]),
    user: (vm) => vm.GET_USER,

    isSupperAdmin: (vm) => vm.user.role == "ADMIN_SUPER",

    tab: {
      get: (vm) => vm.value,
      set(v) {
        this.$emit("input", v);
      },
    },
    paginationCount() {
      return Math.ceil(this.totalItemsCount / this.itemsPerPage);
    },
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },

    tab() {
      this.page = 1;
      this.$nextTick(() => {
        this.getItems();
      });
    },

    isLoading(v) {
      this.$emit("loading", v);
    },
  },

  methods: {
    ...mapActions("admin-masters", [
      "INIT_APPLICATIONS",
      "INIT_BY_ID",
      "EXPORT_EXCEL",
    ]),

    getItems() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.options.status = this.tab;
      this.options.search = this.search;

      this.INIT_APPLICATIONS(this.options).then((totalItemsCount) => {
        this.totalItemsCount = totalItemsCount;
        this.isLoading = false;
      });
    },

    onExport() {
      this.isExport = true;
      this.EXPORT_EXCEL({ status: this.tab }).then(() => {
        this.isExport = false;
      });
    },

    getName: (item) => item?.name,

    getFullName(item) {
      const lastName = item.lastName || "";
      const firstName = item.firstName || "";
      const middleName = item.middleName || "";
      return `${lastName} ${firstName} ${middleName}`;
    },

    getKurs(item) {
      return item.kurs + " kurs";
    },

    getPassport: (item) => item?.passportNumber,

    async onOpen({ id }) {
      const status = await this.INIT_BY_ID({ id });
      if (status) {
        this.$router.push(`/master-show/${id}`);
      }
    },

    onClear() {
      this.search = null;

      this.$nextTick(() => {
        this.getItems();
      });
    },
  },
};
</script>

<template>
  <div>
    <!-- ── Toolbar: Search (left) + Actions (right) ── -->
    <div class="toolbar-row">
      <v-text-field
        :placeholder="$t('Izlash...')"
        append-icon="mdi-magnify"
        v-model="search"
        dense
        hide-details
        outlined
        clearable
        class="search-input"
        @click:append="getItems"
        @keyup.enter="getItems"
        @click:clear="onClear"
      />

      <div class="circle-actions">
        <v-btn
          :hidden="!isSupperAdmin"
          :loading="isExport"
          class="font-made ml-2 modern-button modern-primary"
          :disabled="excelExportDisabled"
          @click="onExport"
        >
          <v-icon left small>mdi-download</v-icon>
          {{ $t("Export") }}
        </v-btn>
      </div>
    </div>

    <v-data-table
      :mobile-breakpoint="0"
      :height="tableHeight"
      :loading="isLoading"
      hide-default-footer
      :headers="[
        {
          text: $t('Full name'),
          value: 'fullName',
        },
        {
          text: $t('Passport'),
          value: 'passportNumber',
        },
        {
          text: $t('Contract Number'),
          value: 'contractNumber',
        },
        {
          text: $t('Group code'),
          value: 'groupCode',
        },
        {
          text: $t('Phone'),
          value: 'mobilePhone',
        },
        {
          text: $t('kurs'),
          value: 'kurs',
        },
      ]"
      fixed-header
      :items="items"
      :options.sync="options"
      :server-items-length="totalItemsCount"
      item-key="id"
      :items-per-page="itemsPerPage"
      :page.sync="page"
      class="elevation-0"
    >
      <template v-slot:body="props">
        <tbody>
          <tr
            @click="onOpen(item)"
            class="cursor-pointer"
            :key="item.id"
            v-for="item in props.items"
          >
            <td v-text="getFullName(item)" />
            <td v-text="item.passportNumber" />
            <td v-text="item.contractNumber" />
            <td v-text="item.groupCode" />
            <td v-text="$tel(item.mobilePhone)" />
            <td v-text="getKurs(item)" />
          </tr>
          <tr v-if="!props.items.length && !isLoading">
            <td :colspan="props.headers.length + 1" class="text-center py-4">
              {{ $t("No data") }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <div class="pt-2 pb-2">
      <v-pagination
        :total-visible="10"
        v-model="page"
        :length="paginationCount"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* ── Toolbar Row ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px 12px;
}

.toolbar-row > .search-input {
  flex: 1;
  min-width: 0;
}

.circle-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  max-width: 100%;
  flex-wrap: wrap;
  row-gap: 10px;
}

.circle-actions > * {
  flex-shrink: 0;
}

/* ── Search Input ── */
.search-input {
  max-width: none;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}

.modern-button {
  text-transform: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.modern-primary {
  background: #1D6F42!important;
  color: #fff;
}
</style>
